import type { ReactNode } from "react";

import type { PROBE_REASONING_EFFORT_OPTIONS } from "../constants/probes";
import type { TEntityId } from "./entity-id";
import type {
  EMcpAuthMode,
  EMcpTransport,
  ENextcloudAuthFlow,
} from "./integration";
import type {
  EModelHostingProvider,
  EModelProvider,
  IBaseModelFields,
  ICreateModelDto,
  IUpdateModelDto,
} from "./models";
import type {
  ETokenBudgetScope,
  ETokenBudgetUnit,
  ETokenBudgetUsageKind,
} from "./token-budget";
import type { ETokenUsageType } from "./token-usage";

export interface IOrganizationOwner {
  uuid: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
}

export interface IOrganizationMcpIntegration {
  title: string;
  url: string;
  isRequiredToken: boolean;
  // NOTE: NSAI-6391 — whether the server has a token stored for this entry. The
  // token itself is never returned. Absent only from a server older than
  // NSAI-6391 (the mobile app can be pointed at an on-premise tenant).
  hasToken?: boolean;
  // NOTE: Only a server older than NSAI-6391 still returns the token.
  token?: string | null;
  isUsedBySpecificModels: boolean;
  specificModelUuids: string[];
  isUsedBySpecificAssistant: boolean;
  specificAssistantUuids: string[];
  // NOTE: NSAI-5354 — optional because every config stored before that change
  // has no transport. Absent is read as AUTO, which probes Streamable HTTP then
  // SSE, so existing SSE servers keep working untouched.
  transport?: EMcpTransport;
  // NOTE: NSAI-5354 — auth scheme. Absent resolves server-side to the legacy
  // behaviour (bearer with a token, nothing without one).
  authMode?: EMcpAuthMode;
  // NOTE: Only meaningful for EMcpAuthMode.HEADER, e.g. "X-API-Key".
  authHeaderName?: string | null;
  isUsedBySpecificProjects?: boolean;
  projectUuids?: string[];
}

/**
 * NOTE: NSAI-5354 — one MCP config as it goes ON THE WIRE. Identical to the parsed
 * form shape except that the two optional credential fields carry null rather than
 * `""` for "absent": a form control must be driven, a stored value must not be an
 * empty string. See toOrganizationMcpPayload.
 */
export interface IOrganizationMcpIntegrationPayload extends Omit<
  IParsedOrganizationMcpIntegration,
  | "authHeaderName"
  | "token"
  | "hasToken"
  | "isTokenReentryRequired"
  | "storedTokenUrl"
> {
  authHeaderName: string | null;
  // NOTE: NSAI-6391 — undefined keeps the stored token (the key is dropped from
  // the JSON body); a string replaces it.
  token?: string;
  // NOTE: NSAI-6391 — the one way to remove a stored token. Sent only to a
  // server that reported `hasToken`: an older one refuses unknown fields.
  isTokenCleared?: boolean;
}

export interface IParsedOrganizationMcpIntegration extends IOrganizationMcpIntegration {
  token: string;
  // NOTE: NSAI-6391 — form state only, never sent: the URL of a row whose
  // token was stored changed, so the token must be entered again.
  isTokenReentryRequired?: boolean;
  // NOTE: NSAI-6391 — form state only, never sent: the URL the stored token
  // belongs to, so a URL edit can tell whether the token still applies.
  storedTokenUrl?: string;
  isUsedBySpecificProjects: boolean;
  projectUuids: string[];
  transport: EMcpTransport;
  authMode: EMcpAuthMode;
  authHeaderName: string;
}

/**
 * NOTE: NSAI-5354 — an MCP server members connected themselves, which the org
 * MCP form could not see.
 *
 * MCP lives in two stores by design: `organizations.mcps` is the OWNER-MANAGED
 * catalog, an `integrations` row is ONE MEMBER's connection to a server in a
 * project. Saving the org form fans the catalog out into connection rows; the
 * reverse never happened, so a server a member added through the catalog
 * `Connect` form's free-form branch was invisible at org level. This is the read
 * that closes that gap, with adopt / remove as the actions on it.
 */
export interface IOrganizationMcpConnection {
  url: string;
  title: string;
  transport: EMcpTransport | null;
  authMode: EMcpAuthMode | null;
  authHeaderName: string | null;
  // NOTE: How many member-to-project connections exist for this URL, so an owner
  // can tell a one-person experiment from something the team relies on.
  connectionCount: number;
  memberCount: number;
  projectUuids: string[];
  isManaged: boolean;
}

// NOTE: NSAI-5354 — one owner-managed MCP server as an approval scope. `scopeKey` is
// `mcp-server:<hash>` and comes from the server: neither web nor mobile can hash a URL
// synchronously while rendering, and re-implementing sha256 per client would risk drifting
// from the key the runtime resolver actually reads.
export interface IOrganizationMcpApprovalTarget {
  scopeKey: string;
  label: string;
  url: string;
}

interface IOrganizationProject {
  uuid: string;
  title: string;
}

export interface ISamlProjectMapping {
  samlGroup: string;
  projectUuid: string;
}

export interface ISamlRoleMapping {
  samlGroup: string;
  roleUuid: string;
  priority: number;
}

// NOTE: What the web and mobile role dialogs need to decide whether to offer the
// SAML group field (NSAI-4590). `canManage` mirrors the server's gate — group
// role mapping switched on AND an org owner — because a non-owner's mapping
// write is always rejected. Built by buildSamlRoleMappingContext.
export interface ISamlRoleMappingContext {
  roleMappings: ISamlRoleMapping[];
  canManage: boolean;
}

// NOTE: A seat tier reuses the subscription product codes, subset PRO | BUSINESS
// (TRIAL excluded). Distinct per-member label, not the org's own subscription.
export type TSamlSeatTier = "pro" | "business";

export const SAML_SEAT_TIER_VALUES: readonly TSamlSeatTier[] = [
  "pro",
  "business",
];

export interface ISamlSeatTierMapping {
  samlGroup: string;
  tier: TSamlSeatTier;
  priority: number;
}

// NOTE: Per-tier seat cap map, e.g. { pro: 20, business: 5 }.
export type TSamlSeatTierLimits = Partial<Record<TSamlSeatTier, number>>;

export type TProvisioningMode = "JIT" | "INVITE_ONLY";

// NOTE: NSAI-5345 — the SAML settings form as both clients hold it; the zod
// schema in schema/organization.ts infers to this shape. Declared here rather
// than derived from the schema so frontend-shared/utils can build it without
// pulling the schema — and its typed translation keys — into every client's
// type-check (the browser extension carries no settings namespace).
export interface ISamlSettingsFormValues {
  projectMappings: ISamlProjectMapping[];
  roleMappings: ISamlRoleMapping[];
  groupRoleMappingEnabled: boolean;
  deprovisioningEnabled: boolean;
  seatTierMappings: ISamlSeatTierMapping[];
  seatTierLimits: TSamlSeatTierLimits;
  seatTierMappingEnabled: boolean;
  provisioningMode: TProvisioningMode;
  requireSso: boolean;
  entryPointUrl?: string;
  issuer?: string;
  certificate?: string;
  domain?: string;
  defaultRoleUuid?: string;
  defaultProjectUuid?: string;
}

export interface IOrganization {
  uuid: string;
  name: string;
  email: string;
  isOwner: boolean;
  owners: IOrganizationOwner[];
  createdAt: string;
  memberCount: number;
  projectCount: number;
  samlEntryPointUrl: string | null;
  samlIssuer: string | null;
  samlCertificate: string | null;
  samlDomain: string | null;
  samlDefaultRoleUuid: string | null;
  samlDefaultProjectUuid: string | null;
  samlProjectMappings: ISamlProjectMapping[];
  samlRoleMappings: ISamlRoleMapping[];
  samlGroupRoleMappingEnabled: boolean;
  samlDeprovisioningEnabled: boolean;
  // NOTE: Seat tiers by group (NSAI-3705/3706). Owner-only; only populated when
  // the SAML_SEAT_TIERS_ENABLED backend flag is on (else empty defaults).
  // `samlSeatTierUsage` is the current per-tier member count for the org.
  samlSeatTierMappings: ISamlSeatTierMapping[];
  samlSeatTierLimits: TSamlSeatTierLimits;
  samlSeatTierMappingEnabled: boolean;
  samlSeatTierUsage: TSamlSeatTierLimits;
  samlProvisioningMode: TProvisioningMode;
  requireSso: boolean;
  bitrix24ClientId: string | null;
  // NOTE: NSAI-6384 — provider secrets are never returned, only whether one is
  // stored. The forms seed the input with STORED_SECRET_PLACEHOLDER when the
  // flag is true and omit the field from the payload unless the owner replaced
  // it; see utils/organization-secrets.
  hasBitrix24ClientSecret: boolean;
  ewsUrl: string | null;
  ewsDomain: string | null;
  hasKonturFocusApiKey: boolean;
  diadocClientId: string | null;
  hasDiadocClientSecret: boolean;
  hasDiadocRefreshToken: boolean;
  jiraDcBaseUrl: string | null;
  jiraDcClientId: string | null;
  hasJiraDcClientSecret: boolean;
  confluenceDcBaseUrl: string | null;
  nextcloudBaseUrl: string | null;
  nextcloudAuthFlow: ENextcloudAuthFlow | null;
  confluenceDcClientId: string | null;
  hasConfluenceDcClientSecret: boolean;
  mcps: IOrganizationMcpIntegration[];
  // NOTE: NSAI-6391 — sent by a server that reports MCP token presence, also
  // when its catalog is empty; absent from an older server.
  isMcpTokenPresenceReported?: boolean;
  // NOTE: Per-org allowlist of custom self-hosted Jitsi domains
  // ("hostname[:port]"). Returned to all members (the editor stays owner-only)
  // so the web instant-join validator can union the user's org domains.
  customJitsiDomains: string[];
  // NOTE: NSAI-6903 — minutes (0–5) before a calendar meeting starts that the
  // auto-joined bot clicks Join; null means as soon as it is ready. Returned
  // to all members like the domains; the editor stays owner-only.
  botJoinMinutesBeforeStart: number | null;
  isWebSearchEnabled: boolean;
  isDeepResearchEnabled: boolean;
  // NOTE: NSAI-6434 — the organization's Deep Research run budget. null hands
  // a value to the deployment's default.
  deepResearchDefaultMode: EDeepResearchMode | null;
  deepResearchMaxRounds: number | null;
  deepResearchTokenBudget: number | null;
  isDeepResearchFanOutAllowed: boolean;
  // NOTE: NSAI-6431 — whether a deep research run may stop to ask clarifying
  // questions first. Defaults to true server-side.
  isDeepResearchClarificationEnabled: boolean;
  isPiiAnonymizationEnabled: boolean;
  // NOTE: Aggregated across the org's projects. true iff at least one
  // project has financial masking on. Strictness is the canonical value
  // from that project; per-chat overrides still apply at the chat boundary.
  isFinancialMaskingEnabled: boolean;
  financialMaskingStrictness: string | null;
  probeValidationModelUuid: string | null;
  // NOTE: NSAI-5201 — owner-only, like the model uuids around it. null means
  // no admin-chosen effort; the probe call uses the model's own default.
  probeValidationReasoningEffort: TProbeReasoningEffort | null;
  probeReroutingModelUuid: string | null;
  probeRetrievalModelUuid: string | null;
  // NOTE: Returned to all members (the model uuids above are owner-only and
  // arrive as null for non-owners). True whenever the org has a validation
  // model configured — the runtime keeps moderating via the seeded default
  // even if that model was later removed.
  isProbeModerationActive: boolean;
  // NOTE: Owner-only. Embedding model selected for cross-chat semantic search;
  // null falls back to the deployment env defaults (CONTEXT_EMBEDDING_*).
  searchEmbeddingModelUuid: string | null;
  isPiiIntentRoutingEnabled: boolean;
  piiIntentClassifierModelUuid: string | null;
  piiTrustedModelUuid: string | null;
  // NOTE: NSAI-6340 — owner-only. Model the chat composer's "Improve prompt"
  // action runs on; null falls back to the deployment's
  // DEFAULT_MODEL_KEY_FOR_PROMPT_IMPROVEMENT env key.
  promptImprovementModelUuid: string | null;
  // NOTE: NSAI-6340 — owner-only. Org default for the multi-model comparison
  // summary. A member's own pick on the Models page still wins; null hands the
  // decision back to the deployment's DEFAULT_MODEL_KEY_FOR_CHAT_TITLE.
  summaryModelUuid: string | null;
  // NOTE: Owner-only. null = retain chats and their uploaded files forever; a
  // positive integer is the number of days before the retention cron deletes
  // them.
  dataRetentionDays: number | null;
}

export type TOrganizationBaseModel = {
  uuid: string;
  project: IOrganizationProject | null;
} & Pick<IBaseModelFields, "name" | "title" | "provider" | "hostingProvider">;

export interface IOrganizationModel extends TOrganizationBaseModel {
  label: string;
  providerLabel: string;
  icon: (props: { className?: string }) => ReactNode;
}

// NOTE: NSAI-4359 — org-OWNED model shapes for the Org Settings "Models" tab
// (owner-only CRUD + per-project visibility matrix). Mirrors the server
// IOrganizationOwnedModel / IOrgModelProjectVisibility wire contract. Distinct
// from IOrganizationModel above (the older "available models" list with a UI
// icon); this one carries no icon and no resolved `enabled` — per-project state
// lives entirely in projectVisibility.
export interface IOrgModelProjectVisibility {
  projectUuid: string;
  projectTitle: string;
  isEnabled: boolean;
}

// NOTE: `baseUrl` (inherited from IBaseModelFields) is a REAL value here, not
// redacted: this is the owner-only management endpoint, so the owner sees the
// endpoint they configured (needed to edit the model). The D8 baseUrl redaction
// applies to the general GET /models listing (getAllModels nulls org rows'
// baseUrl for non-owners), NOT to this owner-gated org-models response. apiKey
// is still never serialized — only hasApiKey.
export interface IOrganizationOwnedModel extends IBaseModelFields {
  uuid: string;
  // NOTE: NSAI-4415 — the D14 models-with-visibility projection returns app
  // rows alongside org-owned rows, so ownerType widened from "organization" to
  // the "app" | "organization" union. App rows are non-CRUD (the row hides
  // Edit / Delete — an app model isn't org-owned) but DO support per-project
  // visibility, so the per-project matrix (Manage visibility) is shown for them
  // too; only org-owned rows additionally get Edit / Delete.
  ownerType: "app" | "organization";
  hasApiKey: boolean;
  createdAt: string;
  updatedAt: string;
  // NOTE: NSAI-4349 — Section 2 of the visibility modal: the Org Owner's
  // per-project decisions (the ORG tier, org_model_visibility with a project_id);
  // a member cannot override them.
  projectVisibility: IOrgModelProjectVisibility[];
  // NOTE: NSAI-4349 — Section 1: the ORG-WIDE master (project_id NULL row).
  // false = the model is hidden in EVERY project and the per-project matrix is
  // gated. OPTIONAL/back-compat: only the models-with-visibility projection
  // populates it; a missing value means enabled (visible org-wide).
  isEnabledForOrg?: boolean;
  // NOTE: NSAI-5880 — true while this platform model is waiting on the
  // organization owner's approval: released after the organization's catalog
  // baseline, with no decision recorded yet. Members do not see the model at
  // all until it is approved; this owner-only projection is where it surfaces.
  // Absent whenever the deployment's approval gate is off.
  isPendingApproval?: boolean;
}

// NOTE: NSAI-4349 — write item for PUT .../org-visibility (the org "Manage
// Visibility" matrix). Each item writes the ORG tier for one project — no
// projectTitle (that is response-only, on IOrgModelProjectVisibility).
export interface IModelOrgVisibilityItem {
  projectUuid: string;
  isEnabled: boolean;
}

export interface ISetModelOrgVisibilityBody {
  items: IModelOrgVisibilityItem[];
}

// NOTE: param objects for the OrganizationApi org-model methods (repo rule:
// single object arg). `uuid` is the org uuid; `data` is the payload — matching
// the role-CRUD precedent (createRole/updateRole/deleteRole).
export interface ICreateOrgModelParams {
  uuid: string;
  data: ICreateModelDto;
}

export interface IUpdateOrgModelParams {
  uuid: string;
  modelUuid: string;
  data: IUpdateModelDto;
}

export interface IDeleteOrgModelParams {
  uuid: string;
  modelUuid: string;
}

// NOTE: NSAI-4349 — param object for PUT
// .../org-models/:modelUuid/org-visibility (the org "Manage Visibility" matrix
// write, Section 2). `data.items` sets the ORG tier per project — the Org
// Owner's authoritative visibility decision a project member cannot override.
export interface ISetOrgModelOrgVisibilityParams {
  uuid: string;
  modelUuid: string;
  data: ISetModelOrgVisibilityBody;
}

// NOTE: NSAI-4349 — param object for PATCH
// .../org-models/:modelUuid/org-wide-visibility (the Section-1 org-wide master).
// isEnabled=false hides the model in every project and gates the matrix.
// Disabling a model still in use rejects with 409 { message, usage[] }.
export interface ISetOrgModelOrgWideVisibilityParams {
  uuid: string;
  modelUuid: string;
  data: {
    isEnabled: boolean;
  };
}

// NOTE: NSAI-5880 — param object for POST .../model-approvals, the owner's
// approve / decline of newly released platform models. `modelUuids` is a list
// so "Approve all" is one request rather than one per model — a release can
// introduce several at once, and a partial failure mid-loop would leave the
// owner looking at a half-decided list.
export interface ISetModelApprovalParams {
  uuid: string;
  data: {
    modelUuids: string[];
    isApproved: boolean;
  };
}

// NOTE: NSAI-4630 — read-only "where is this model used" view for the org
// "View usage" modal. `references` are assistants + automations (each with its
// project + creator); `settings` are the org-level model-slot settings pointing
// at the model (org-scoped, no per-user creator); `probeReferenceCount` counts
// per-project probe references. Project Default / Summary model are absent —
// they live in browser localStorage, not the DB, so the server cannot list them.
// NOTE: NSAI-6434 — how much research a Deep Research run does: "quick" is
// one search round, "deep" the full budget. Mirrors the server's EResearchMode.
export enum EDeepResearchMode {
  QUICK = "quick",
  DEEP = "deep",
}

// NOTE: NSAI-6434 — the Deep Research budget form's state on both clients:
// the mode select ("default" = the deployment's), two free-text numbers (empty
// = the deployment's default) and the fan-out toggle.
export type TDeepResearchModeOption = EDeepResearchMode | "default";

export interface IDeepResearchBudgetDraft {
  mode: TDeepResearchModeOption;
  maxRounds: string;
  tokenBudget: string;
  isFanOutAllowed: boolean;
}

export type TDeepResearchBudgetField = "maxRounds" | "tokenBudget";

export interface IDeepResearchBudgetDraftErrors {
  maxRounds?: boolean;
  tokenBudget?: boolean;
}

export interface IParsedDeepResearchBudget {
  deepResearchDefaultMode: EDeepResearchMode | null;
  deepResearchMaxRounds: number | null;
  deepResearchTokenBudget: number | null;
  isDeepResearchFanOutAllowed: boolean;
}

export interface IParseDeepResearchBudgetResult {
  // NOTE: null when a field is invalid; `errors` names which.
  budget: IParsedDeepResearchBudget | null;
  errors: IDeepResearchBudgetDraftErrors;
}

export interface IDeepResearchBudgetRange {
  readonly min: number;
  readonly max: number;
}

export interface IParseDeepResearchBoundedValueProps {
  value: string;
  range: IDeepResearchBudgetRange;
}

export interface IBuildDeepResearchBudgetPayloadProps {
  budget: IParsedDeepResearchBudget;
}

export enum EOrgModelUsageRefType {
  ASSISTANT = "assistant",
  CRON = "cron",
  WEBHOOK = "webhook",
  HOOK = "hook",
  WORKFLOW = "workflow",
}

export interface IOrgModelUsageReference {
  type: EOrgModelUsageRefType;
  name: string;
  projectTitle: string;
  createdByName: string | null;
  isPrivate?: boolean;
}

export enum EOrgModelUsageSettingSlot {
  PROBE_VALIDATION = "probe_validation",
  REROUTING = "rerouting",
  RETRIEVAL = "retrieval",
  FINANCIAL_MASKING = "financial_masking",
  SEARCH_EMBEDDING = "search_embedding",
  PII_INTENT_CLASSIFIER = "pii_intent_classifier",
  PII_TRUSTED = "pii_trusted",
  PROMPT_IMPROVEMENT = "prompt_improvement",
  SUMMARY = "summary",
  DEFAULT_PROBE = "default_probe",
}

export interface IOrgModelUsageSetting {
  slot: EOrgModelUsageSettingSlot;
}

// NOTE: NSAI-4637 — deployment-wide system operations (env/const configured,
// resolved by model key) that back a model. Mirrors the server enum in
// server/src/models/models.system-reserved.ts (ESystemModelUsage) — keep in sync.
export enum ESystemModelUsage {
  CHAT_TITLE = "chat_title",
  MULTI_MODEL_SUMMARY = "multi_model_summary",
  PROMPT_IMPROVEMENT = "prompt_improvement",
  ANONYMIZATION_LAB = "anonymization_lab",
  KNOWLEDGE_RETRIEVAL_GATE = "knowledge_retrieval_gate",
  CONTEXT_SUMMARY = "context_summary",
  USER_MEMORY = "user_memory",
  PRESENTATION_ROUTING = "presentation_routing",
  PRESENTATION_IMAGE = "presentation_image",
  PROBE_VALIDATION = "probe_validation",
}

// NOTE: NSAI-7721 — i18n keys of the notes shown for system usages that never
// block a disable (see SYSTEM_MODEL_USAGE_DISABLE_NOTE_KEYS).
export type TSystemModelUsageDisableNoteKey =
  | "organizationModels.usage.system_note.presentation_routing"
  | "organizationModels.usage.system_note.presentation_image";

export interface IOrgModelUsage {
  references: IOrgModelUsageReference[];
  settings: IOrgModelUsageSetting[];
  probeReferenceCount: number;
  // NOTE: NSAI-4637 — deployment-wide system operations that resolve this model.
  systemUsages: ESystemModelUsage[];
}

export interface IGetOrgModelUsageParams {
  uuid: string;
  modelUuid: string;
}

// NOTE: NSAI-4415 — shape of the 409 body returned when disabling an org model
// that is still in use. `usage` lists the human-readable areas still relying on
// the model (e.g. "3 assistants", "1 automation") so the UI can name them.
export interface IModelInUseErrorData {
  message: string;
  usage: string[];
}

export interface IOrganizationAssistant {
  uuid: string;
  avatar: string | null;
  name: string;
  project: IOrganizationProject;
}

export interface IOrganizationMember {
  uuid: string;
  user: {
    uuid: string;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
  };
  role: {
    uuid: string;
    name: string;
  };
  project: IOrganizationProject;
  joinedAt: string;
}

export interface ICreateOrganizationBody {
  name: string;
  email: string;
  verificationCode?: string;
}

export interface IEditOrganizationBody {
  name?: string;
  email?: string;
}

export interface IEditOrganizationParams {
  uuid: string;
  data: IEditOrganizationBody;
}

export interface ITransferOrganizationOwnershipPayload {
  email: string;
  verificationCode?: string;
  removeCurrentOwner?: boolean;
}

export interface ITransferOrganizationOwnershipParams {
  uuid: string;
  data: ITransferOrganizationOwnershipPayload;
}

export interface IRemoveOrganizationOwnerPayload {
  email: string;
  // NOTE: Role the ex-owner is left with. Optional on the wire so older clients
  // still work — the server falls back to Collaborator rather than leaving them
  // on the Admin role granted at promotion time.
  roleUuid?: string;
}

export interface IRemoveOrganizationOwnerParams {
  uuid: string;
  data: IRemoveOrganizationOwnerPayload;
}

// NOTE: NSAI-6227 — removal from the WHOLE organization, and so from every one
// of its projects. Addresses the member by user uuid: organization membership
// is one project_member row per project, so no single member uuid names them.
export interface IRemoveOrganizationMemberPayload {
  userUuid: string;
}

export interface IRemoveOrganizationMemberParams {
  uuid: string;
  data: IRemoveOrganizationMemberPayload;
}

// NOTE: NSAI-6515 — an organization-scoped ban. Like removal it names the
// person by user uuid; unlike removal it sticks — SSO, invitations and a
// project admin's add all refuse the person until an owner lifts the ban.
export interface IOrganizationMemberBanPayload {
  userUuid: string;
}

export interface IOrganizationMemberBanParams {
  uuid: string;
  data: IOrganizationMemberBanPayload;
}

export interface IOrganizationBannedMemberUser {
  uuid: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
}

// NOTE: NSAI-6515 — one row of GET /organizations/:uuid/banned-members.
// `bannedBy` is null once the banning owner's account is gone.
export interface IOrganizationBannedMember {
  uuid: string;
  user: IOrganizationBannedMemberUser;
  bannedBy: IOrganizationBannedMemberUser | null;
  bannedAt: string;
}

export interface IUpdateOrganizationIntegrationConfigPayload {
  bitrix24ClientId?: string | null;
  bitrix24ClientSecret?: string | null;
  ewsUrl?: string | null;
  ewsDomain?: string | null;
  konturFocusApiKey?: string | null;
  diadocClientId?: string | null;
  diadocClientSecret?: string | null;
  diadocRefreshToken?: string | null;
  jiraDcBaseUrl?: string | null;
  jiraDcClientId?: string | null;
  jiraDcClientSecret?: string | null;
  confluenceDcBaseUrl?: string | null;
  nextcloudBaseUrl?: string | null;
  nextcloudAuthFlow?: ENextcloudAuthFlow | null;
  confluenceDcClientId?: string | null;
  confluenceDcClientSecret?: string | null;
  mcps: Array<{
    title: string;
    url: string;
    isRequiredToken: boolean;
    transport?: EMcpTransport;
    authMode?: EMcpAuthMode;
    authHeaderName?: string | null;
    // NOTE: NSAI-6391 — omitted keeps the stored token; see
    // IOrganizationMcpIntegrationPayload.
    token?: string | null;
    isTokenCleared?: boolean;
    isUsedBySpecificModels: boolean;
    specificModelUuids: string[];
    isUsedBySpecificAssistant: boolean;
    specificAssistantUuids: string[];
    isUsedBySpecificProjects: boolean;
    projectUuids: string[];
  }>;
}

export interface IUpdateOrganizationIntegrationConfigParams {
  uuid: string;
  data: IUpdateOrganizationIntegrationConfigPayload;
}

export interface IUpdateOrganizationSamlPayload {
  entryPointUrl: string | null;
  issuer: string | null;
  certificate: string | null;
  domain: string | null;
  defaultRoleUuid: string | null;
  defaultProjectUuid: string | null;
  projectMappings: ISamlProjectMapping[];
  roleMappings: ISamlRoleMapping[];
  groupRoleMappingEnabled: boolean;
  deprovisioningEnabled: boolean;
  seatTierMappings: ISamlSeatTierMapping[];
  seatTierLimits: TSamlSeatTierLimits;
  seatTierMappingEnabled: boolean;
  provisioningMode: TProvisioningMode;
  requireSso: boolean;
}

export interface IUpdateOrganizationSamlParams {
  uuid: string;
  data: IUpdateOrganizationSamlPayload;
}

export type TVerifiedDomainStatus = "pending" | "verified";

export type TVerifiedDomainMethod = "dns_txt" | "email";

export interface IVerifiedDomainDnsInstructions {
  host: string;
  type: "TXT";
  value: string;
}

export interface IVerifiedDomain {
  uuid: string;
  domain: string;
  status: TVerifiedDomainStatus;
  verificationMethod: TVerifiedDomainMethod;
  verifiedAt: string | null;
  createdAt: string;
  dns: IVerifiedDomainDnsInstructions;
}

export interface IAddVerifiedDomainParams {
  uuid: string;
  data: {
    domain: string;
  };
}

export interface IVerifyVerifiedDomainParams {
  uuid: string;
  domainUuid: string;
}

export interface IRemoveVerifiedDomainParams {
  uuid: string;
  domainUuid: string;
}

export type TOrganizationInviteStatus =
  | "pending"
  | "consumed"
  | "expired"
  | "revoked";

export interface IOrganizationInvite {
  uuid: string;
  email: string;
  status: TOrganizationInviteStatus;
  roleUuid: string;
  roleName: string | null;
  projectUuid: string;
  projectTitle: string;
  invitedByUserId: TEntityId | null;
  expiresAt: string;
  createdAt: string;
}

export interface IResendOrganizationInviteParams {
  uuid: string;
  inviteUuid: string;
}

export interface IRevokeOrganizationInviteParams {
  uuid: string;
  inviteUuid: string;
}

export interface IUpdateOrganizationChatSettingsPayload {
  // NOTE: NSAI-6434 — optional: absent leaves the stored toggle as is.
  isWebSearchEnabled?: boolean;
  isDeepResearchEnabled?: boolean;
  // NOTE: NSAI-6431 — optional; omitted leaves the stored value unchanged.
  isDeepResearchClarificationEnabled?: boolean;
  isPiiAnonymizationEnabled?: boolean;
  isFinancialMaskingEnabled?: boolean;
  // NOTE: null clears the strictness override (back to BALANCED). undefined
  // omits the field (preserves the previously stored value).
  financialMaskingStrictness?: string | null;
  // NOTE: NSAI-6434 — absent leaves a value as is; null hands it back to the
  // deployment's default.
  deepResearchDefaultMode?: EDeepResearchMode | null;
  deepResearchMaxRounds?: number | null;
  deepResearchTokenBudget?: number | null;
  isDeepResearchFanOutAllowed?: boolean;
}

export interface IUpdateOrganizationChatSettingsParams {
  uuid: string;
  data: IUpdateOrganizationChatSettingsPayload;
}

export interface IUpdateCallAssistantSettingsPayload {
  // NOTE: Full replace of the org's custom self-hosted Jitsi domain allowlist
  // ("hostname[:port]"). Server normalizes (trim/lowercase/dedupe).
  customJitsiDomains: string[];
  // NOTE: NSAI-6903 — null clears it. The server keeps the stored value when
  // the field is absent (clients that predate it); required here so both
  // clients always send what the form shows.
  botJoinMinutesBeforeStart: number | null;
}

export interface IUpdateCallAssistantSettingsParams {
  uuid: string;
  data: IUpdateCallAssistantSettingsPayload;
}

export interface ICallAssistantSettingsResponse {
  customJitsiDomains: string[];
  botJoinMinutesBeforeStart: number | null;
}

// NOTE: NSAI-6903 — one entry of the bot join time picker; `value` is what
// toBotJoinTimeOptionValue gives (utils/bot-join-time.ts).
export interface IBotJoinTimeOption {
  value: string;
  label: string;
}

export interface IProbeModelDefaults {
  validationModel: { uuid: string; title: string; isDefault: boolean } | null;
  reroutingModel: { uuid: string; title: string } | null;
  retrievalModel: { uuid: string; title: string } | null;
  piiIntentClassifierModel: { uuid: string; title: string } | null;
  piiTrustedModel: { uuid: string; title: string } | null;
  // NOTE: NSAI-6340 — the deployment's configured "Improve prompt" model,
  // reported whatever the org chose, because it is what the rewrite falls back
  // to: the org's own choice is `promptImprovementModelUuid` on the
  // organization payload. null when the deployment's env key names a model this
  // installation never seeded.
  promptImprovementDefaultModel: { uuid: string; title: string } | null;
  // NOTE: NSAI-6340 — the deployment's summary model, the last step of the
  // chain (member's pick -> org default -> this). Named on the picker's default
  // option, same as the field above.
  summaryDefaultModel: { uuid: string; title: string } | null;
  isPiiIntentRoutingEnabled: boolean;
}

// NOTE: NSAI-5201 — the effort values an admin may pick for the probe slot.
// Derived from the shared option list so the picker, the payload type and the
// server DTO cannot drift apart.
export type TProbeReasoningEffort =
  (typeof PROBE_REASONING_EFFORT_OPTIONS)[number];

export interface IUpdateProbeModelSettingsPayload {
  probeValidationModelUuid?: string | null;
  // NOTE: NSAI-5201 — reasoning effort for the probe moderation call.
  // null clears the override (effort falls back to the model's own default);
  // undefined omits the field and preserves the stored value.
  probeValidationReasoningEffort?: TProbeReasoningEffort | null;
  probeReroutingModelUuid?: string | null;
  probeRetrievalModelUuid?: string | null;
  piiIntentClassifierModelUuid?: string | null;
  piiTrustedModelUuid?: string | null;
  // NOTE: NSAI-6340 — null clears the org override and hands "Improve prompt"
  // back to the deployment default; undefined leaves the stored value alone.
  promptImprovementModelUuid?: string | null;
  // NOTE: NSAI-6340 — null clears the org default and lets members without a
  // pick fall through to the deployment model; undefined leaves it alone.
  summaryModelUuid?: string | null;
  isPiiIntentRoutingEnabled?: boolean;
}

export interface IUpdateProbeModelSettingsParams {
  uuid: string;
  data: IUpdateProbeModelSettingsPayload;
}

export interface IUpdateOrganizationSearchSettingsPayload {
  // NOTE: null clears the override (search falls back to env defaults);
  // a UUID selects an embedding model.
  searchEmbeddingModelUuid?: string | null;
}

export interface IUpdateOrganizationSearchSettingsParams {
  uuid: string;
  data: IUpdateOrganizationSearchSettingsPayload;
}

// NOTE: NSAI-5664 — mirrors the server's ESearchEmbeddingModelState: whether
// the selected search embedding model is the one embeddings actually go to.
export enum ESearchEmbeddingModelState {
  DEFAULT = "default",
  ACTIVE = "active",
  UNUSABLE = "unusable",
}

// NOTE: NSAI-5664 — mirrors the server's ESearchEmbeddingModelIssue.
export enum ESearchEmbeddingModelIssue {
  NOT_ELIGIBLE = "not_eligible",
  PROVIDER_ERROR = "provider_error",
  DIMENSION_MISMATCH = "dimension_mismatch",
}

export interface IOrganizationSearchEmbeddingStatus {
  state: ESearchEmbeddingModelState;
  issue?: ESearchEmbeddingModelIssue;
}

// NOTE: NSAI-5036 — mirrors the server's ETokenLimitPeriod. The month-family
// values are whole CALENDAR months (quarter = 3, half_year = 6, year = 12), so a
// budget window never drifts against the calendar the way a fixed day count
// would.
export type TTokenLimitPeriod =
  | "week"
  | "month"
  | "quarter"
  | "half_year"
  | "year";

export interface IProjectUsageLimitItem {
  projectUuid: string;
  title: string;
  tokenLimitTotal: number | null;
  tokenLimitAgents: number | null;
  costLimitTotal: number | null;
  costLimitAgents: number | null;
}

// NOTE: NSAI-5612 — `cost*` caps are USD and independent of the token ones: an
// owner can set either denomination, both, or neither, and each binds on its
// own. null means that denomination is uncapped at this scope.
export interface IOrganizationUsageLimits {
  tokenLimitTotal: number | null;
  tokenLimitAgents: number | null;
  tokenLimitPerUser: number | null;
  tokenLimitAgentsPerUser: number | null;
  costLimitTotal: number | null;
  costLimitAgents: number | null;
  costLimitPerUser: number | null;
  costLimitAgentsPerUser: number | null;
  tokenLimitPeriod: TTokenLimitPeriod;
  projects: IProjectUsageLimitItem[];
}

export interface IProjectUsageLimitPayload {
  projectUuid: string;
  tokenLimitTotal: number | null;
  tokenLimitAgents: number | null;
  costLimitTotal: number | null;
  costLimitAgents: number | null;
}

export interface IUpdateOrganizationUsageLimitsPayload {
  tokenLimitTotal: number | null;
  tokenLimitAgents: number | null;
  tokenLimitPerUser: number | null;
  tokenLimitAgentsPerUser: number | null;
  costLimitTotal: number | null;
  costLimitAgents: number | null;
  costLimitPerUser: number | null;
  costLimitAgentsPerUser: number | null;
  tokenLimitPeriod: TTokenLimitPeriod;
  projectLimits: IProjectUsageLimitPayload[];
}

export interface IUpdateOrganizationUsageLimitsParams {
  uuid: string;
  data: IUpdateOrganizationUsageLimitsPayload;
}

// NOTE: NSAI-3376 — member-facing read-only view of the org usage limits.
// Returned by GET /organizations/:uuid/usage-limits/overview. Only configured
// caps appear as items; empty arrays mean "no limit set". `remainingAmount` is
// clamped at zero server-side, and every figure is denominated by `unit`
// (NSAI-5612) — the same cap can be set in tokens, in USD, or both, and each
// denomination arrives as its own item.
export interface IOrganizationUsageLimitOverviewItem {
  scope: ETokenBudgetScope;
  usageKind: ETokenBudgetUsageKind;
  unit: ETokenBudgetUnit;
  // NOTE: NSAI-6268 — null where the dimension is being consumed WITHOUT a cap
  // (an organization on a dollar-only budget still spends tokens). Nothing is
  // "remaining" of an absent limit, so that is null too — rendering 0 there
  // would read as an exhausted budget.
  limitAmount: number | null;
  usedAmount: number;
  remainingAmount: number | null;
}

// NOTE: NSAI-6357 — what one dimension has consumed this period, for EVERY
// dimension the owner's usage-limits form can cap: the organization's four, the
// requesting member's four, and four per project, in both denominations,
// whether or not a cap is configured there.
//
// Carried beside the display lists rather than read out of them because those
// answer a different question. They list what the budget summary should render,
// which omits a dimension nobody capped — and an omitted item is indistinguish-
// able from zero usage, so the below-usage guard could not tell "nothing used"
// from "nothing to compare against" and stayed silent while a first-ever cap
// below current usage saved and locked the scope out.
//
// COMPLETE BY CONSTRUCTION, and that is the property to rely on: every dimension
// is measured server-side, and an entry is omitted only when its usage is truly
// zero. Read a missing entry as ZERO USED, never as unknown. (Zero entries are
// dropped to keep the payload flat on organizations with many projects; a limit
// can never be typed as zero, so a zero measurement can never warn about
// anything.)
export interface IOrganizationUsageMeasurement {
  scope: ETokenBudgetScope;
  usageKind: ETokenBudgetUsageKind;
  unit: ETokenBudgetUnit;
  usedAmount: number;
  // NOTE: The project a PROJECT-scoped measurement belongs to; null for the
  // organization- and member-scoped ones.
  projectUuid: string | null;
}

// NOTE: NSAI-3377 — one project's configured caps with their consumption. Only
// projects with a cap set are present.
export interface IOrganizationUsageLimitOverviewProject {
  projectUuid: string;
  title: string;
  items: IOrganizationUsageLimitOverviewItem[];
}

export interface IOrganizationUsageLimitsOverview {
  tokenLimitPeriod: TTokenLimitPeriod;
  periodStart: string;
  periodEnd: string;
  organization: IOrganizationUsageLimitOverviewItem[];
  personal: IOrganizationUsageLimitOverviewItem[];
  projects: IOrganizationUsageLimitOverviewProject[];
  // NOTE: NSAI-6357 — current consumption for every cappable dimension, which
  // is what the owner form's below-usage guard compares a typed cap against.
  // The three lists above are a DISPLAY selection and omit uncapped dimensions;
  // this one omits nothing that has been used.
  measuredUsage: IOrganizationUsageMeasurement[];
  // NOTE: NSAI-6266 — server-supplied so the sentence naming the exclusions
  // cannot drift from the rule producing them; see IOrganizationUsageStatus in
  // the server's token-budget.types.ts for why it is served rather than
  // mirrored here.
  excludedUsageTypes: ETokenUsageType[];
  // NOTE: NSAI-6266 — what the per-member ("Your usage") figures leave out. A
  // superset of the list above: the per-member total also ignores agent usage
  // (NSAI-5796), so disclosing only the shared list beside that figure would
  // understate its gap against Token & Cost Usage.
  personalExcludedUsageTypes: ETokenUsageType[];
}

// NOTE: NSAI-5037 — PER-REQUEST caps for one model, distinct from the token
// BUDGETS above (which cap cumulative spend over a period). A null cap means "no
// cap at this scope"; projectUuid null identifies the ORG-WIDE default applied to
// every project.
export interface IModelTokenLimitConfig {
  modelUuid: string;
  projectUuid: string | null;
  maxInputTokens: number | null;
  maxOutputTokens: number | null;
}

// NOTE: contextSize / maxTokens are the model's OWN ceilings, sent so the UI can
// show what a cap is being tightened against.
// NOTE: name / provider / hostingProvider exist so the caps matrix can be ordered
// with the SAME shared sortModelsByPriority + getProviderLabel the model picker
// uses, rather than a second ordering rule that would drift from it.
export interface IModelTokenLimitModelItem {
  modelUuid: string;
  title: string;
  name: string;
  provider: EModelProvider;
  hostingProvider: EModelHostingProvider;
  contextSize: number;
  maxTokens: number;
}

export interface IModelTokenLimitProjectItem {
  projectUuid: string;
  title: string;
  // NOTE: NSAI-5654 — the project's own PERIOD budget, so the form can reject a
  // per-request cap above it inline instead of finding out on save. null means
  // the project sets none and inherits the organization's.
  tokenLimitTotal: number | null;
}

export interface IOrganizationModelTokenLimits {
  // NOTE: NSAI-5037 — the server's feature flag, carried in the payload so the
  // client never keeps its own copy that could drift. False means the deployment
  // has the feature off: the collections are empty and the section is not shown.
  isEnabled: boolean;
  models: IModelTokenLimitModelItem[];
  projects: IModelTokenLimitProjectItem[];
  limits: IModelTokenLimitConfig[];
  // NOTE: NSAI-5654 — the organization's PERIOD budget. A per-request cap above
  // it can never be satisfied (the budget binds first), and the neighbouring
  // budget fields already reject that shape, so the form validates against it
  // inline. null means no budget is configured, which bounds nothing.
  organizationTokenLimitTotal: number | null;
}

// NOTE: Full-payload contract, like the usage-limits payload: `limits` is every
// cap the owner wants to exist. An entry with both caps null CLEARS that cap.
export interface IUpdateOrganizationModelTokenLimitsPayload {
  limits: IModelTokenLimitConfig[];
}

export interface IUpdateOrganizationModelTokenLimitsParams {
  uuid: string;
  data: IUpdateOrganizationModelTokenLimitsPayload;
}

export interface IOrganizationDataRetention {
  dataRetentionDays: number | null;
}

export interface IUpdateOrganizationDataRetentionPayload {
  dataRetentionDays: number | null;
}

export interface IUpdateOrganizationDataRetentionParams {
  uuid: string;
  data: IUpdateOrganizationDataRetentionPayload;
}

export interface IOrganizationRole {
  uuid: string;
  name: string;
  permissions: Record<string, string[]>;
}

export interface ICreateOrganizationRoleBody {
  name: string;
  permissions: string[];
  // NOTE: Optional SAML group this role is created for (NSAI-4590). When set,
  // the server appends a group->role mapping to the org's SAML config in the
  // same request, so a role meant for SSO no longer has to be wired up in a
  // second trip to the SAML tab. Owner-only server-side.
  samlGroup?: string;
}

export interface IUpdateOrganizationRoleBody {
  name?: string;
  permissions?: string[];
}

export interface ICreateOrganizationRoleParams {
  uuid: string;
  data: ICreateOrganizationRoleBody;
}

export interface IUpdateOrganizationRoleParams {
  uuid: string;
  roleUuid: string;
  data: IUpdateOrganizationRoleBody;
}

export interface IDeleteOrganizationRoleParams {
  uuid: string;
  roleUuid: string;
}

export interface IDeleteOrganizationParams {
  uuid: string;
  verificationCode?: string;
}

export interface ISyncatorApiKey {
  uuid: string;
  name: string;
  keyPrefix: string;
  targetProjectId: TEntityId | null;
  isActive: boolean;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface ISyncatorApiKeyCreateResponse extends ISyncatorApiKey {
  key: string;
}

export interface ICreateSyncatorApiKeyParams {
  organizationUuid: string;
  data: {
    name: string;
    targetProjectId?: TEntityId;
  };
}

export interface IRevokeSyncatorApiKeyParams {
  organizationUuid: string;
  keyUuid: string;
}

// NOTE: NSAI-3060 — the organization's own SIEM export configuration, read and
// written by the organization owner through Settings. The transports an owner
// may select deliberately exclude the backend's "stdout" route: it writes to the
// platform process's own output, which a tenant cannot read. The read view still
// carries it, because support can set it from the admin panel and the screen has
// to render an organization that is already on it.
export const ORGANIZATION_SIEM_TRANSPORTS = [
  "syslog",
  "tls-syslog",
  "http",
] as const;

export type TOrganizationSiemTransport =
  (typeof ORGANIZATION_SIEM_TRANSPORTS)[number];

export type TOrganizationSiemTransportView =
  | TOrganizationSiemTransport
  | "stdout";

// NOTE: TLS key material is write-only across the whole stack — the API accepts
// it and never echoes it back, so the view reports only whether each PEM is
// stored. isConfigured=false means the organization has no row yet: the screen
// shows defaults rather than an empty saved state.
// NOTE: NSAI-5147 — no organizationId. The owner routes are addressed by
// organization UUID and the response used to echo the internal bigint primary
// key back, which is the one database integer id that crossed a tenant
// boundary. Nothing on the screen ever read it.
export interface IOrganizationSiemConfig {
  isConfigured: boolean;
  isEnabled: boolean;
  transport: TOrganizationSiemTransportView | null;
  syslogHost: string | null;
  syslogPort: number | null;
  httpEndpoint: string | null;
  tlsRejectUnauthorized: boolean | null;
  hasTlsCa: boolean;
  hasTlsCert: boolean;
  hasTlsKey: boolean;
  // NOTE: NSAI-6548 — when the stored client certificate stops being valid
  // (ISO), null when none is stored or it does not parse. Optional because a
  // client can meet a server from before the field existed (a mobile build
  // against an older on-premise install): absent reads as unknown, and the
  // screen then says nothing about expiry rather than something wrong.
  tlsCertValidTo?: string | null;
  // NOTE: NSAI-6481 — the header an HTTP collector authenticates with. The
  // name is shown (null reads as "Authorization"); the value is a credential
  // and, like the PEMs, is reported only as on file or not. Optional for the
  // same older-server reason as tlsCertValidTo.
  httpAuthHeaderName?: string | null;
  hasHttpAuthHeaderValue?: boolean;
}

// NOTE: A partial patch: an omitted field is left as-is, an explicit null clears
// the stored column. That distinction is what lets the screen save the
// connection without resending certificates it was never given.
export interface IUpdateOrganizationSiemConfigPayload {
  isEnabled?: boolean;
  transport?: TOrganizationSiemTransport;
  syslogHost?: string | null;
  syslogPort?: number | null;
  httpEndpoint?: string | null;
  tlsRejectUnauthorized?: boolean;
  tlsCaPem?: string | null;
  tlsCertPem?: string | null;
  tlsKeyPem?: string | null;
  // NOTE: NSAI-6481 — null clears the name back to "Authorization" and the
  // value off file; the value is write-only like the PEMs.
  httpAuthHeaderName?: string | null;
  httpAuthHeaderValue?: string | null;
}

// NOTE: NSAI-6369 — the whole workspace exclusion list, every time. A full
// replacement rather than a patch, because the editor is a textarea holding
// the complete policy and that is what the admin is looking at.
export interface IUpdateOrganizationMemoryExclusionsParams {
  uuid: string;
  categories: string[];
}

export interface IUpdateOrganizationSiemConfigParams {
  uuid: string;
  data: IUpdateOrganizationSiemConfigPayload;
}

// NOTE: NSAI-5354 — params for the owner-only MCP connection endpoints. Only the
// server's identity is sent on adopt: the promoted entry lands with conservative
// defaults (no org token, no scoping) and the owner edits it in the MCP form like
// any other, so no member's credential is ever copied into org-wide config.
export interface IAdoptOrganizationMcpConnectionPayload {
  url: string;
  title?: string;
}

export interface IAdoptOrganizationMcpConnectionParams {
  uuid: string;
  data: IAdoptOrganizationMcpConnectionPayload;
}

export interface IDeleteOrganizationMcpConnectionPayload {
  url: string;
}

export interface IDeleteOrganizationMcpConnectionParams {
  uuid: string;
  data: IDeleteOrganizationMcpConnectionPayload;
}

export interface ITestOrganizationSiemConfigParams {
  uuid: string;
  data: IUpdateOrganizationSiemConfigPayload;
}
